/**
 * GraphBrowser - List-based relationship graph browser
 *
 * Shows sorted list of people with relationship strength bars.
 * Features: search/filter, friends-only toggle, sort by (strength/name/recent).
 * Click person → expands to show PersonContext inline.
 *
 * @module @humanizer/studio/components/archive/GraphBrowser
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useGraphData, type GraphPerson, type GraphDataOptions } from '../../hooks/useGraphData';
import { PersonContextPanel } from './PersonContext';

interface GraphBrowserProps {
  onOpenContent?: (nodeId: string) => void;
}

export function GraphBrowser({ onOpenContent }: GraphBrowserProps) {
  const {
    people,
    loading,
    error,
    selectedPerson,
    selectedPersonLoading,
    fetch,
    selectPerson,
    clearSelection,
  } = useGraphData();

  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<'strength' | 'name' | 'recent'>('strength');
  const [friendsOnly, setFriendsOnly] = useState(false);
  const [expandedPersonId, setExpandedPersonId] = useState<string | null>(null);

  // Fetch on mount and when filters change
  useEffect(() => {
    const options: GraphDataOptions = { sortBy, friendsOnly };
    if (search.trim()) options.search = search.trim();
    fetch(options);
  }, [sortBy, friendsOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search
  useEffect(() => {
    const timeout = setTimeout(() => {
      const options: GraphDataOptions = { sortBy, friendsOnly };
      if (search.trim()) options.search = search.trim();
      fetch(options);
    }, 300);
    return () => clearTimeout(timeout);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePersonClick = useCallback((person: GraphPerson) => {
    if (expandedPersonId === person.id) {
      setExpandedPersonId(null);
      clearSelection();
    } else {
      setExpandedPersonId(person.id);
      selectPerson(person.id);
    }
  }, [expandedPersonId, selectPerson, clearSelection]);

  const handleOpenContent = useCallback((nodeId: string) => {
    onOpenContent?.(nodeId);
  }, [onOpenContent]);

  // Compute max strength for bar widths
  const maxStrength = people.reduce((max, p) => Math.max(max, p.relationshipStrength), 1);

  if (error) {
    return (
      <div className="graph-browser graph-browser--error">
        <p className="graph-browser__error">{error}</p>
        <button className="graph-browser__retry" onClick={() => fetch()}>Retry</button>
      </div>
    );
  }

  return (
    <div className="graph-browser">
      {/* Search and controls */}
      <div className="graph-browser__controls">
        <input
          type="text"
          className="graph-browser__search"
          placeholder="Search people..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="graph-browser__filters">
          <select
            className="graph-browser__sort"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'strength' | 'name' | 'recent')}
          >
            <option value="strength">Strongest</option>
            <option value="name">A-Z</option>
            <option value="recent">Recent</option>
          </select>
          <label className="graph-browser__toggle">
            <input
              type="checkbox"
              checked={friendsOnly}
              onChange={(e) => setFriendsOnly(e.target.checked)}
            />
            <span>Friends only</span>
          </label>
        </div>
      </div>

      {/* Loading */}
      {loading && people.length === 0 && (
        <div className="graph-browser__loading">Loading graph data...</div>
      )}

      {/* Empty state */}
      {!loading && people.length === 0 && (
        <div className="graph-browser__empty">
          <p>No relationship data found.</p>
          <p className="graph-browser__empty-hint">Import a Facebook export to build the relationship graph.</p>
        </div>
      )}

      {/* People list */}
      <div className="graph-browser__list">
        {people.map((person) => (
          <div key={person.id} className="graph-browser__item-wrapper">
            <button
              className={`graph-browser__person ${expandedPersonId === person.id ? 'graph-browser__person--expanded' : ''}`}
              onClick={() => handlePersonClick(person)}
            >
              <div className="graph-browser__person-info">
                <span className="graph-browser__person-name">{person.name}</span>
                <div className="graph-browser__person-badges">
                  {person.isFriend && <span className="graph-browser__badge graph-browser__badge--friend">Friend</span>}
                  {person.isFollower && <span className="graph-browser__badge">Follower</span>}
                  {person.isFollowing && <span className="graph-browser__badge">Following</span>}
                </div>
              </div>
              <div className="graph-browser__strength">
                <div
                  className="graph-browser__strength-bar"
                  style={{ width: `${Math.max(2, (person.relationshipStrength / maxStrength) * 100)}%` }}
                />
                <span className="graph-browser__strength-value">
                  {person.relationshipStrength.toFixed(0)}
                </span>
              </div>
              <div className="graph-browser__person-stats">
                {person.edgeCount > 0 && <span>{person.edgeCount} interactions</span>}
                {person.tagCount > 0 && <span>{person.tagCount} tags</span>}
              </div>
            </button>

            {/* Expanded person context */}
            {expandedPersonId === person.id && selectedPerson && (
              <PersonContextPanel
                data={selectedPerson}
                loading={selectedPersonLoading}
                onOpenContent={handleOpenContent}
                onClose={() => { setExpandedPersonId(null); clearSelection(); }}
              />
            )}
            {expandedPersonId === person.id && !selectedPerson && selectedPersonLoading && (
              <div className="graph-person-context graph-person-context--loading">Loading...</div>
            )}
          </div>
        ))}
      </div>

      {/* Summary */}
      {people.length > 0 && (
        <div className="graph-browser__summary">
          {people.length} {friendsOnly ? 'friends' : 'people'} shown
        </div>
      )}
    </div>
  );
}
